import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  OverlayDrawer,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerBody,
  DrawerFooter,
  Field,
  Combobox,
  Option,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { BasicButton } from '@components/common/BasicButton';
import { ENTER_KEY, SPACE_KEY } from '@utils/keyboard';
import { useRoomFilterPanelStyles } from './RoomFilterPanel.styles';

export interface RoomFilters {
  buildingIds: string[];
  roomIds: string[];
  roomTypes: string[];
  capacities: string[];
  amenities: string[];
  availability: string[];
}

export const EMPTY_ROOM_FILTERS: RoomFilters = {
  buildingIds: [],
  roomIds: [],
  roomTypes: [],
  capacities: [],
  amenities: [],
  availability: [],
};

export interface RoomOption {
  spaceId: string;
  spaceName: string;
  buildingId?: string;
}

/**
 * Filters items by a search query, then sorts so currently-selected items appear first
 * (preserving the original order within each group). Exported for unit testing.
 */
export const filterAndSortBySelected = <T,>(
  items: readonly T[],
  search: string | undefined,
  isSelected: (item: T) => boolean,
  matchesQuery: (item: T, q: string) => boolean
): T[] => {
  const q = search?.trim().toLowerCase() ?? '';
  const matches = q.length === 0 ? [...items] : items.filter((it) => matchesQuery(it, q));
  const selectedFirst = matches.filter(isSelected);
  const rest = matches.filter((it) => !isSelected(it));
  return [...selectedFirst, ...rest];
};

export interface RoomFilterPanelProps {
  open: boolean;
  appliedFilters: RoomFilters;
  availableRooms: RoomOption[];
  buildingNameById?: Record<string, string>;
  availableRoomTypes?: string[];
  availableCapacities?: string[];
  availableAmenities?: string[];
  availableAvailability?: string[];
  onApply: (filters: RoomFilters) => void;
  onCancel: () => void;
}

export const RoomFilterPanel = ({
  open,
  appliedFilters,
  availableRooms,
  buildingNameById,
  onApply,
  onCancel,
}: RoomFilterPanelProps) => {
  const styles = useRoomFilterPanelStyles();
  const [localFilters, setLocalFilters] = useState<RoomFilters>(EMPTY_ROOM_FILTERS);
  const [roomSearch, setRoomSearch] = useState<string | undefined>(undefined);
  const [buildingSearch, setBuildingSearch] = useState<string | undefined>(undefined);
  const [roomIdSnapshot, setRoomIdSnapshot] = useState<Set<string>>(new Set());
  const [buildingIdSnapshot, setBuildingIdSnapshot] = useState<Set<string>>(new Set());
  const [selectedBuildingIds, setSelectedBuildingIds] = useState<string[]>([]);

  const buildingOptions = useMemo(() => {
    const seen = new Map<string, string>();
    // Include all planner-accessible buildings from the name lookup
    if (buildingNameById) {
      for (const [id, name] of Object.entries(buildingNameById)) {
        seen.set(id, name);
      }
    }
    // Fallback: include buildings from rooms if not already covered
    for (const room of availableRooms) {
      if (room.buildingId && !seen.has(room.buildingId)) {
        seen.set(room.buildingId, room.buildingId);
      }
    }
    return Array.from(seen.entries())
      .map(([id, name]) => ({ buildingId: id, buildingName: name }))
      .sort((a, b) => a.buildingName.localeCompare(b.buildingName));
  }, [availableRooms, buildingNameById]);

  const filteredBuildingOptions = useMemo(
    () =>
      filterAndSortBySelected(
        buildingOptions,
        buildingSearch,
        (opt) => buildingIdSnapshot.has(opt.buildingId),
        (opt, q) => opt.buildingName.toLowerCase().includes(q)
      ),
    [buildingOptions, buildingSearch, buildingIdSnapshot]
  );

  const prevBuildingCountRef = useRef(0);
  const skipBuildingEffectRef = useRef(false);

  useEffect(() => {
    if (open) {
      setLocalFilters({ ...appliedFilters });
      setRoomSearch(undefined);
      setBuildingSearch(undefined);
      setRoomIdSnapshot(new Set(appliedFilters.roomIds));
      setBuildingIdSnapshot(new Set(appliedFilters.buildingIds ?? []));
      setSelectedBuildingIds(appliedFilters.buildingIds ?? []);
      prevBuildingCountRef.current = appliedFilters.buildingIds?.length ?? 0;
      skipBuildingEffectRef.current = true;
    }
  }, [open, appliedFilters]);

  useEffect(() => {
    if (skipBuildingEffectRef.current) {
      skipBuildingEffectRef.current = false;
      return;
    }
    const prevCount = prevBuildingCountRef.current;
    prevBuildingCountRef.current = selectedBuildingIds.length;

    if (selectedBuildingIds.length === 0) {
      if (prevCount > 0) {
        setLocalFilters((prev) => {
          if (prev.roomIds.length === 0) return prev;
          return { ...prev, roomIds: [] };
        });
      }
      return;
    }
    const buildingSet = new Set(selectedBuildingIds);
    const validRoomIds = new Set(
      availableRooms
        .filter((r) => r.buildingId && buildingSet.has(r.buildingId))
        .map((r) => r.spaceId)
    );
    setLocalFilters((prev) => {
      const filtered = prev.roomIds.filter((id) => validRoomIds.has(id));
      if (filtered.length === prev.roomIds.length) return prev;
      return { ...prev, roomIds: filtered };
    });
  }, [selectedBuildingIds, availableRooms]);

  const handleChange = useCallback(
    (key: keyof RoomFilters) => (_e: unknown, data: { selectedOptions: string[] }) => {
      setLocalFilters((prev) => ({ ...prev, [key]: data.selectedOptions }));
    },
    []
  );

  const handleClear = useCallback(
    (key: keyof RoomFilters) => () => {
      setLocalFilters((prev) => ({ ...prev, [key]: [] }));
    },
    []
  );

  const handleApply = useCallback(() => {
    const roomIds = selectedBuildingIds.length === 0 ? [] : localFilters.roomIds;
    onApply({ ...localFilters, roomIds, buildingIds: selectedBuildingIds });
  }, [localFilters, selectedBuildingIds, onApply]);

  const buildingSelectedText = useMemo(() => {
    if (selectedBuildingIds.length === 0) return '';
    return selectedBuildingIds.map((id) => buildingNameById?.[id] ?? id).join(', ');
  }, [selectedBuildingIds, buildingNameById]);

  const roomOptions = useMemo(
    () =>
      availableRooms.map((r) => ({
        ...r,
        label: r.spaceName,
      })),
    [availableRooms]
  );

  const filteredRoomOptions = useMemo(() => {
    const buildingSet = new Set(selectedBuildingIds);
    const buildingFiltered =
      buildingSet.size > 0
        ? roomOptions.filter((opt) => opt.buildingId && buildingSet.has(opt.buildingId))
        : roomOptions;
    return filterAndSortBySelected(
      buildingFiltered,
      roomSearch,
      (opt) => roomIdSnapshot.has(opt.spaceId),
      (opt, q) => opt.label.toLowerCase().includes(q)
    );
  }, [roomOptions, roomSearch, roomIdSnapshot, selectedBuildingIds]);

  const roomSelectedText = useMemo(() => {
    if (localFilters.roomIds.length === 0) return '';
    const labelById = new Map(roomOptions.map((o) => [o.spaceId, o.label]));
    return localFilters.roomIds.map((id) => labelById.get(id) ?? id).join(', ');
  }, [localFilters.roomIds, roomOptions]);

  return (
    <OverlayDrawer
      position="end"
      open={open}
      onOpenChange={(_e, data) => {
        if (!data.open) onCancel();
      }}
      data-testid="room-filter-panel"
    >
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <BasicButton
              variant="subtle"
              aria-label="Close"
              icon={<Dismiss24Regular />}
              onClick={onCancel}
              data-testid="room-filter-panel-close"
            />
          }
        >
          Filter
        </DrawerHeaderTitle>
      </DrawerHeader>

      <DrawerBody className={styles.drawerBody}>
        <div className={styles.fieldGroup}>
          <Field label="Building">
            <Combobox
              multiselect
              autoComplete="off"
              placeholder="Not selected"
              value={buildingSearch ?? buildingSelectedText}
              onChange={(e) => setBuildingSearch((e.target as HTMLInputElement).value)}
              onOpenChange={(_e, data) => {
                if (data.open) {
                  setBuildingSearch('');
                  setBuildingIdSnapshot(new Set(selectedBuildingIds));
                } else {
                  setBuildingSearch(undefined);
                }
              }}
              selectedOptions={selectedBuildingIds}
              onOptionSelect={(_e, data) => setSelectedBuildingIds(data.selectedOptions)}
              data-testid="filter-building"
              listbox={{ className: styles.comboboxListbox }}
            >
              {filteredBuildingOptions.map((opt) => (
                <Option key={opt.buildingId} value={opt.buildingId} text={opt.buildingName}>
                  {opt.buildingName}
                </Option>
              ))}
              {filteredBuildingOptions.length === 0 && (
                <Option key="__no_match__" value="__no_match__" disabled text="">
                  No results
                </Option>
              )}
            </Combobox>
            {selectedBuildingIds.length > 0 && (
              <span
                role="button"
                tabIndex={0}
                className={styles.clearSelectionButton}
                onClick={() => {
                  setSelectedBuildingIds([]);
                  setLocalFilters((prev) => ({ ...prev, roomIds: [] }));
                }}
                onKeyDown={(e) => {
                  if (e.key === ENTER_KEY || e.key === SPACE_KEY) {
                    setSelectedBuildingIds([]);
                    setLocalFilters((prev) => ({ ...prev, roomIds: [] }));
                  }
                }}
                data-testid="clear-building-selection"
              >
                Clear selection
              </span>
            )}
          </Field>
          <Field label="Room name">
            <Combobox
              multiselect
              autoComplete="off"
              disabled={selectedBuildingIds.length === 0}
              placeholder={
                selectedBuildingIds.length === 0
                  ? 'Select a building first'
                  : localFilters.roomIds.length > 0
                    ? 'Search rooms...'
                    : 'Not selected'
              }
              selectedOptions={localFilters.roomIds}
              onOptionSelect={handleChange('roomIds')}
              value={roomSearch ?? roomSelectedText}
              onChange={(e) => setRoomSearch((e.target as HTMLInputElement).value)}
              onOpenChange={(_e, data) => {
                if (data.open) {
                  setRoomSearch('');
                  setRoomIdSnapshot(new Set(localFilters.roomIds));
                } else {
                  setRoomSearch(undefined);
                }
              }}
              data-testid="filter-room-name"
              listbox={{ className: styles.comboboxListbox }}
            >
              {filteredRoomOptions.map((opt) => (
                <Option key={opt.spaceId} value={opt.spaceId} text={opt.label}>
                  {opt.label}
                </Option>
              ))}
              {filteredRoomOptions.length === 0 && (
                <Option key="__no_match__" value="__no_match__" disabled text="">
                  No results
                </Option>
              )}
            </Combobox>
            {localFilters.roomIds.length > 0 && (
              <span
                role="button"
                tabIndex={0}
                className={styles.clearSelectionButton}
                onClick={handleClear('roomIds')}
                onKeyDown={(e) => {
                  if (e.key === ENTER_KEY || e.key === SPACE_KEY) handleClear('roomIds')();
                }}
              >
                Clear selection
              </span>
            )}
          </Field>
        </div>
      </DrawerBody>

      <DrawerFooter className={styles.footer}>
        <BasicButton variant="primary" onClick={handleApply} data-testid="room-filter-apply">
          Apply
        </BasicButton>
        <BasicButton variant="secondary" onClick={onCancel} data-testid="room-filter-cancel">
          Cancel
        </BasicButton>
      </DrawerFooter>
    </OverlayDrawer>
  );
};

RoomFilterPanel.displayName = 'RoomFilterPanel';
